/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewPCDetails.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState } from "react";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import SaveIcon from "@mui/icons-material/Save";
import CustomFormTextField from "../common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../common/select/CustomSelectV2";
import CloseIcon from "@mui/icons-material/Close";
import ImageBox from "./ImageBox";
function ViewPCDetails({
  selectedProjectDetails,
  projectDetails,
  handleEditClick, // optional external handler if needed
}) {
  const formatLabel = (str) => {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) =>
        word === "sop" ? "SOP" : word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join(" ");
  };

  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState("");

  const handleStartEdit = (item) => {
    setEditingField(item.label);
    setEditedValue(item.value);
  };

  const handleSave = (label) => {
    if (handleEditClick) handleEditClick(label, editedValue);
    setEditingField(null);
  };

  return (
    <Box>
      {/* 🔹 Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          mb: 1.5,
        }}
      >
        <ImageBox boxes={selectedProjectDetails} />
      </Box>

      {/* 🔹 Title + Owner */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          {selectedProjectDetails?.name?.toUpperCase()}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            color: "text.primary",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          Owner - {selectedProjectDetails?.owner_details?.[0]?.name}
        </Typography>
      </Box>

      <Divider />

      {/* 🔹 Key–Value Section */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          rowGap: 4,
          columnGap: 4,
          mt: 2,
          maxHeight: "140px",
          minHeight: "120px",
          overflowY: "auto",

          /* 🔥 Modern thin scrollbar */
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#b0b0b0",
            borderRadius: "6px",
          },
        }}
      >

        {projectDetails?.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: "1 1 calc(50% - 16px)",
              minWidth: "45%",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 500,
                  color: "text.secondary",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                {formatLabel(item?.label)}
              </Typography>

              {editingField !== item.label ? (
                item?.editable && (
                  <IconButton size="small" onClick={() => handleStartEdit(item)}>
                    <BorderColorIcon sx={{ color: "#000", fontSize: 16 }} />
                  </IconButton>
                )
              ) : (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    disabled={
                      !editedValue ||
                      editedValue === item?.value
                    }
                    size="small"
                    onClick={() => handleSave(item.label)}
                    sx={{ color: "#000" }}
                  >
                    <SaveIcon sx={{ fontSize: 18 }} />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => setEditingField(null)}
                    sx={{ color: "#000" }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/* Value / Editable Field */}
            {editingField === item.label ? (
              <Box sx={{ mt: 1 }}>
                {item?.label === "Project Status" ? (
                  <CustomSelectV2
                    showLabel={false}
                    required={false}
                    handleOnChange={(e) => setEditedValue(e.target.value)}
                    selectedValue={editedValue}
                    optionsList={item?.optionList || []}
                  />
                ) : item.label === "Trademark" ? (
                  <CustomFormTextField
                    type="text"
                    fldType="text"
                    value={editedValue}
                    setValue={setEditedValue}
                  />
                ) : null}
              </Box>
            ) : (
              <Typography
                variant="subtitle2"
                textAlign={"start"}
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontFamily: "Manrope, sans-serif",
                }}
              >
                {item?.value || "-"}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default ViewPCDetails;
